import { useQuery } from '@tanstack/react-query'
import { api, type ArticleDetail, type Jump, type UnitDetail } from '../../lib/api'
import { LOW_CONFIDENCE, wordCountOf, type RegionItem } from './model'

/** The API prefixes its own label ("cols 3-5 of 5"); the panel adds the word. */
function bareColumns(label: string | null | undefined): string | null {
  return label ? label.replace(/^cols?\s+/i, '') : null
}

/**
 * The record behind the selected region.
 *
 * `/api/units/{id}` is the contract's answer for every type and is asked for
 * first. Where it has not landed, an article region falls back to
 * `/api/articles/{id}`, and anything else is described from the blocks the
 * page already carried. The panel therefore never waits on a field and never
 * shows one it does not have.
 */

export interface RegionRecord {
  title: string | null
  subtitle: string | null
  byline: string | null
  isWire: boolean
  wireService: string | null
  wordCount: number
  confidence: number | null
  correctionSource: string | null
  articleId: string | null
  unitId: string | null
  jump: Jump | null
  citation: Record<string, string | null> | null
  permalink: string | null
  needsReview: boolean
  columnsLabel: string | null
  /** Where the record came from, so the panel never claims more than it has. */
  source: 'unit' | 'article' | 'blocks'
}

function fromUnit(unit: UnitDetail): RegionRecord {
  return {
    title: unit.title,
    subtitle: unit.subtitle,
    byline: unit.byline,
    isWire: unit.is_wire,
    wireService: unit.wire_service,
    wordCount: unit.word_count ?? 0,
    confidence: unit.confidence,
    correctionSource: unit.correction_source,
    articleId: unit.article_id,
    unitId: unit.id,
    jump: unit.jump ?? null,
    citation: unit.citation ?? null,
    permalink: unit.permalink,
    needsReview:
      unit.confidence !== null && unit.confidence !== undefined && unit.confidence < LOW_CONFIDENCE,
    columnsLabel: bareColumns(unit.columns_label),
    source: 'unit',
  }
}

function fromArticle(article: ArticleDetail): RegionRecord {
  return {
    title: article.headline,
    subtitle: article.deck ?? article.subhead,
    byline: article.byline,
    isWire: article.is_wire,
    wireService: article.wire_service,
    wordCount: article.word_count ?? 0,
    confidence: article.confidence,
    correctionSource: article.correction_source,
    articleId: article.id,
    unitId: article.unit_id ?? null,
    jump: article.jump ?? null,
    citation: article.citation ?? null,
    permalink: null,
    needsReview: article.needs_review,
    columnsLabel: null,
    source: 'article',
  }
}

function fromBlocks(region: RegionItem): RegionRecord {
  return {
    title: region.title,
    subtitle: null,
    byline: null,
    isWire: false,
    wireService: null,
    wordCount: wordCountOf(region),
    confidence: region.confidence,
    correctionSource: null,
    articleId: region.articleId,
    unitId: region.unitId,
    jump: null,
    citation: null,
    permalink: null,
    needsReview:
      region.confidence !== null &&
      region.confidence !== undefined &&
      region.confidence < LOW_CONFIDENCE,
    columnsLabel: null,
    source: 'blocks',
  }
}

export function useRegionDetail(region: RegionItem | null): RegionRecord | null {
  const unit = useQuery({
    queryKey: ['unit', region?.unitId],
    queryFn: () => api.get<UnitDetail>(`/units/${region!.unitId}`),
    enabled: !!region?.unitId,
    retry: false,
  })

  const article = useQuery({
    queryKey: ['article', region?.articleId],
    queryFn: () => api.get<ArticleDetail>(`/articles/${region!.articleId}`),
    enabled: !!region?.articleId && !unit.data,
    retry: false,
  })

  if (!region) return null
  const base = unit.data
    ? fromUnit(unit.data)
    : article.data
      ? fromArticle(article.data)
      : fromBlocks(region)
  /* A field the detail endpoint left null is still known from the page, and a
     panel that shows nothing where the page knows something is a panel that
     lies by omission. */
  return {
    ...base,
    title: base.title ?? region.title,
    confidence: base.confidence ?? region.confidence,
    wordCount: base.wordCount || wordCountOf(region),
    needsReview:
      base.needsReview ||
      (region.confidence !== null &&
        region.confidence !== undefined &&
        region.confidence < LOW_CONFIDENCE),
  }
}
